import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, Truck, CheckCircle, XCircle, Clock, AlertCircle, 
  Search, RefreshCw, RotateCcw, ArrowLeftRight, DollarSign,
  Filter, ChevronDown, User, Phone, Mail, MapPin, Calendar,
  PackageCheck, Warehouse, ClipboardCheck, Ban, PackageX,
  CheckCircle2, Loader2, Plus, FileText, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';
import ReturnDetailPanel from '@/components/ReturnDetailPanel';
import ReturnsAnalyticsDialog from '@/components/ReturnsAnalyticsDialog';
import { BarChart3 } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Return status tabs configuration
const returnTabs = [
  { id: 'all', label: 'All', icon: Package, description: 'All returns' },
  { id: 'pending', label: 'Pending', icon: Clock, description: 'Awaiting review' },
  { id: 'accepted', label: 'Accepted', icon: CheckCircle, description: 'Approved returns' },
  { id: 'pickup_assigned', label: 'Pickup Assigned', icon: Truck, description: 'Pickup scheduled' },
  { id: 'picked_up,in_transit', label: 'In Transit', icon: Truck, description: 'On the way back' },
  { id: 'delivered,qc_pending', label: 'QC Pending', icon: ClipboardCheck, description: 'Ready for QC' },
  { id: 'inwarded', label: 'Inwarded', icon: Warehouse, description: 'QC completed' },
  { id: 'refund_pending', label: 'Refund Pending', icon: DollarSign, description: 'Awaiting refund' },
  { id: 'completed', label: 'Completed', icon: CheckCircle2, description: 'Fully processed' },
  { id: 'rejected', label: 'Rejected', icon: Ban, description: 'Rejected returns' },
];

// Return status styling
const statusConfig = {
  pending: { label: 'Pending', color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', icon: Clock },
  accepted: { label: 'Accepted', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', icon: Ban },
  cancelled: { label: 'Cancelled', color: 'text-zinc-400', bg: 'bg-zinc-500/10', border: 'border-zinc-500/30', icon: Ban },
  pickup_assigned: { label: 'Pickup Assigned', color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', icon: Truck },
  picked_up: { label: 'Picked Up', color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30', icon: Package },
  in_transit: { label: 'In Transit', color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30', icon: Truck },
  delivered: { label: 'Delivered', color: 'text-teal-400', bg: 'bg-teal-500/10', border: 'border-teal-500/30', icon: PackageCheck },
  qc_pending: { label: 'QC Pending', color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30', icon: ClipboardCheck },
  inwarded: { label: 'Inwarded', color: 'text-indigo-400', bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', icon: Warehouse },
  refund_pending: { label: 'Refund Pending', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30', icon: DollarSign },
  completed: { label: 'Completed', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30', icon: CheckCircle2 },
  rto_in_transit: { label: 'RTO In Transit', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', icon: RotateCcw },
};

// Return type styling
const returnTypeConfig = {
  return_refund: { label: 'Return & Refund', color: 'text-blue-400', bg: 'bg-blue-500/10', icon: RotateCcw },
  refund_only: { label: 'Refund Only', color: 'text-green-400', bg: 'bg-green-500/10', icon: DollarSign },
  replace_same: { label: 'Replace (Same)', color: 'text-purple-400', bg: 'bg-purple-500/10', icon: ArrowLeftRight },
  replace_different: { label: 'Replace (Different)', color: 'text-orange-400', bg: 'bg-orange-500/10', icon: ArrowLeftRight },
};

const ReturnOrdersPage = () => {
  const { user } = useAuth();
  const { formatCurrency } = useSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(searchParams.get('status') || 'all');
  const [returnTypeFilter, setReturnTypeFilter] = useState('all');
  const [statusCounts, setStatusCounts] = useState({});
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalReturns, setTotalReturns] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 50;
  
  // Detail panel
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  
  // Fetch returns
  const fetchReturns = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (activeTab !== 'all') {
        params.append('status', activeTab);
      }
      if (returnTypeFilter !== 'all') {
        params.append('return_type', returnTypeFilter);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      params.append('page', page.toString());
      params.append('limit', PAGE_SIZE.toString());
      
      const response = await axios.get(`${API_BASE_URL}/v1/returns?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReturns(response.data.returns || []);
      setTotalReturns(response.data.total || 0);
      setStatusCounts(response.data.status_counts || {});
      setHasMore(response.data.returns?.length === PAGE_SIZE);
    } catch (error) {
      console.error('Error fetching returns:', error);
      toast.error('Failed to load returns');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [activeTab, returnTypeFilter, searchTerm, page]);
  
  useEffect(() => {
    fetchReturns();
  }, [fetchReturns]);
  
  // Update URL when tab changes
  useEffect(() => {
    if (activeTab !== 'all') {
      setSearchParams({ status: activeTab });
    } else {
      setSearchParams({});
    }
    setPage(1);
  }, [activeTab, setSearchParams]);
  
  const handleRefresh = () => {
    fetchReturns(true);
  };
  
  const handleReturnClick = (returnItem) => {
    setSelectedReturn(returnItem);
    setDetailPanelOpen(true);
  };
  
  const handleReturnUpdated = () => {
    fetchReturns(true);
    // Optionally close panel or refresh selected return
    if (selectedReturn) {
      // Refresh the selected return details
      fetchReturnDetails(selectedReturn.id);
    }
  };
  
  const fetchReturnDetails = async (returnId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/v1/returns/${returnId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedReturn(response.data.return);
    } catch (error) {
      console.error('Error fetching return details:', error);
    }
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit'
    });
  };
  
  // Get tab count
  const getTabCount = (tabId) => {
    if (tabId === 'all') {
      return Object.values(statusCounts).reduce((a, b) => a + b, 0);
    }
    const statuses = tabId.split(',');
    return statuses.reduce((sum, s) => sum + (statusCounts[s] || 0), 0);
  };
  
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-[#0a0a0f]/95 backdrop-blur-xl border-b border-white/5">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl font-semibold text-white flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-purple-400" />
                Return Orders
              </h1>
              <p className="text-sm text-zinc-500 mt-0.5">
                {totalReturns} total returns
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setAnalyticsOpen(true)}
                variant="outline"
                size="sm"
                className="border-white/10 bg-white/5 hover:bg-white/10"
                data-testid="returns-analytics-btn"
              >
                <BarChart3 className="w-4 h-4 mr-1" /> Analytics
              </Button>
              <Button
                onClick={handleRefresh}
                variant="outline"
                size="sm"
                className="border-white/10 bg-white/5 hover:bg-white/10"
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
          
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search by return #, order #, customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-zinc-500"
                data-testid="return-search-input"
              />
            </div>
            <Select value={returnTypeFilter} onValueChange={setReturnTypeFilter}>
              <SelectTrigger className="w-[180px] bg-white/5 border-white/10 text-white" data-testid="return-type-filter">
                <SelectValue placeholder="Return Type" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="return_refund">Return & Refund</SelectItem>
                <SelectItem value="refund_only">Refund Only</SelectItem>
                <SelectItem value="replace_same">Replace (Same)</SelectItem>
                <SelectItem value="replace_different">Replace (Different)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Status Tabs */}
        <div className="px-4 overflow-x-auto scrollbar-hide">
          <div className="flex gap-1 pb-2 min-w-max">
            {returnTabs.map((tab) => {
              const count = getTabCount(tab.id);
              const isActive = activeTab === tab.id;
              const TabIcon = tab.icon;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all
                    ${isActive 
                      ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' 
                      : 'text-zinc-400 hover:text-white hover:bg-white/5'
                    }
                  `}
                  data-testid={`return-tab-${tab.id}`}
                >
                  <TabIcon className="w-4 h-4" />
                  <span>{tab.label}</span>
                  {count > 0 && (
                    <span className={`
                      text-xs px-1.5 py-0.5 rounded-full
                      ${isActive ? 'bg-purple-500/30 text-purple-200' : 'bg-white/10 text-zinc-400'}
                    `}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Returns List */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
          </div>
        ) : returns.length === 0 ? (
          <div className="text-center py-12">
            <PackageX className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400">No returns found</p>
            <p className="text-sm text-zinc-600 mt-1">Returns will appear here when customers request them</p>
          </div>
        ) : (
          <div className="space-y-3">
            <AnimatePresence mode="popLayout">
              {returns.map((returnItem, index) => {
                const status = statusConfig[returnItem.status] || statusConfig.pending;
                const returnType = returnTypeConfig[returnItem.return_type] || returnTypeConfig.return_refund;
                const StatusIcon = status.icon;
                const TypeIcon = returnType.icon;
                
                return (
                  <motion.div
                    key={returnItem.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ delay: index * 0.03 }}
                    onClick={() => handleReturnClick(returnItem)}
                    className="bg-white/[0.02] border border-white/5 rounded-xl p-4 hover:bg-white/[0.04] hover:border-white/10 cursor-pointer transition-all group"
                    data-testid={`return-card-${returnItem.id}`}
                  >
                    {/* Return Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-sm font-medium text-white">
                            {returnItem.return_number}
                          </span>
                          <Badge className={`${status.bg} ${status.color} border ${status.border} text-xs`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {status.label}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-zinc-500">
                          <span className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            Order: {returnItem.order_number}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(returnItem.created_at)}
                          </span>
                        </div>
                      </div>
                      <Badge className={`${returnType.bg} ${returnType.color} text-xs`}>
                        <TypeIcon className="w-3 h-3 mr-1" />
                        {returnType.label}
                      </Badge>
                    </div>
                    
                    {/* Customer Info */}
                    <div className="flex items-center gap-4 mb-3 text-sm">
                      <div className="flex items-center gap-1.5 text-zinc-300">
                        <User className="w-4 h-4 text-zinc-500" />
                        {returnItem.customer_name}
                      </div>
                      {returnItem.customer_phone && (
                        <div className="flex items-center gap-1.5 text-zinc-500">
                          <Phone className="w-3.5 h-3.5" />
                          {returnItem.customer_phone}
                        </div>
                      )}
                    </div>
                    
                    {/* Items Summary */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1.5 text-sm text-zinc-400">
                          <Package className="w-4 h-4" />
                          {returnItem.items?.length || 0} item{(returnItem.items?.length || 0) !== 1 ? 's' : ''}
                        </div>
                        {returnItem.pickup_awb && (
                          <Badge variant="outline" className="text-xs text-zinc-500 border-zinc-700">
                            AWB: {returnItem.pickup_awb}
                          </Badge>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-white">
                          {formatCurrency(returnItem.refund_amount || 0)}
                        </div>
                        <div className="text-xs text-zinc-500">Refund Amount</div>
                      </div>
                    </div>
                    
                    {/* Quick Actions on Hover */}
                    <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                      <div className="text-xs text-zinc-600">
                        {returnItem.source === 'customer_portal' ? 'Customer Portal' : 
                         returnItem.source === 'shopify' ? 'Shopify' : 'Manual'}
                      </div>
                      <div className="flex items-center gap-2">
                        {['pending', 'accepted', 'pickup_assigned', 'picked_up', 'in_transit'].includes(returnItem.status) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async (e) => {
                              e.stopPropagation();
                              const reason = window.prompt('Reason for cancelling this return (e.g., customer changed their mind):');
                              if (reason === null) return;
                              try {
                                const token = localStorage.getItem('token');
                                await axios.post(`${API_BASE_URL}/v1/returns/${returnItem.id}/cancel`,
                                  { reason: reason || 'Customer no longer wants to return' },
                                  { headers: { Authorization: `Bearer ${token}` } });
                                toast.success('Return cancelled');
                                fetchReturns(true);
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to cancel return');
                              }
                            }}
                            className="border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20 hover:text-red-200 h-7 px-2 text-xs"
                            data-testid={`cancel-return-btn-${returnItem.id}`}
                          >
                            <Ban className="w-3.5 h-3.5 mr-1" />
                            Cancel Return
                          </Button>
                        )}
                        {['delivered', 'pickup_assigned', 'picked_up', 'in_transit'].includes(returnItem.status) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/orders/inwarding?create_return_id=${returnItem.id}`);
                            }}
                            className="border-orange-500/30 bg-orange-500/10 text-orange-300 hover:bg-orange-500/20 hover:text-orange-200 h-7 px-2 text-xs"
                            data-testid={`create-manifest-btn-${returnItem.id}`}
                          >
                            <Warehouse className="w-3.5 h-3.5 mr-1" />
                            Create Manifest
                          </Button>
                        )}
                        {returnItem.status === 'qc_pending' && returnItem.inwarding_manifest_id && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/orders/inwarding?qc_manifest_id=${returnItem.inwarding_manifest_id}`);
                            }}
                            className="border-orange-500/30 bg-orange-500/10 text-orange-300 hover:bg-orange-500/20 hover:text-orange-200 h-7 px-2 text-xs"
                            data-testid={`process-qc-btn-${returnItem.id}`}
                          >
                            <ClipboardCheck className="w-3.5 h-3.5 mr-1" />
                            Process QC
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 h-7 px-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" />
                          View Details
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
            
            {/* Load More */}
            {hasMore && returns.length > 0 && (
              <div className="text-center pt-4">
                <Button
                  variant="outline"
                  onClick={() => setPage(p => p + 1)}
                  className="border-white/10 bg-white/5 hover:bg-white/10"
                  data-testid="load-more-returns"
                >
                  Load More
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Return Detail Panel */}
      <ReturnsAnalyticsDialog open={analyticsOpen} onOpenChange={setAnalyticsOpen} />
      <ReturnDetailPanel
        returnData={selectedReturn}
        isOpen={detailPanelOpen}
        onClose={() => {
          setDetailPanelOpen(false);
          setSelectedReturn(null);
        }}
        onReturnUpdated={handleReturnUpdated}
      />
    </div>
  );
};

export default ReturnOrdersPage;
